/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { clearAttentiveCookies, setAttentiveAnalyticsConsent } from '@/components/attentive/attentive.utils';
import { deleteCookiesByPrefix } from '@/lib/cookie-utils.client';
import { createLogger } from '@/lib/logger';
import { clearSessionJSONItem, getSessionJSONItem, setSessionJSONItem } from '@/lib/utils';
import { resourceRoutes } from '@/route-paths';
import { TrackingConsent } from '@/types/tracking-consent';

import { injectAdobeLaunch, isAdobeLaunchLoaded } from './adobe-launch.utils';
import { CATEGORY_COOKIE_PREFIXES, OneTrustCategory, getActiveGroups, hasCategory } from './onetrust.utils';

const logger = createLogger();

/**
 * Session-storage key holding the last `dw_dnt` value we successfully posted, so a consent callback
 * on every page load doesn't re-post an unchanged value. The action route refreshes the SLAS token,
 * which is far too expensive to repeat per navigation.
 */
const DNT_SYNC_KEY = 'onetrust:dw-dnt';

/**
 * Reload guard. Revoking C0002 after Adobe Launch has already loaded is the one case that needs a
 * reload — a tag manager cannot be unloaded — and this makes sure it happens at most once per
 * document, so a misconfiguration can never turn into a reload loop.
 */
let hasReloadedForRevokedPerformance = false;

/**
 * The `dw_dnt` value currently being posted, if any.
 *
 * A single consent change produces TWO callbacks in the same tick — `OptanonWrapper` re-runs when
 * the preference centre saves, and `OnConsentChanged` fires for the same save. {@link DNT_SYNC_KEY}
 * is only written once the request resolves, so on its own it cannot stop the second caller: both
 * read the stale value and both post. This reservation is claimed synchronously, which is what
 * makes the guard actually hold. (Verified against the live SDK — without it every consent change
 * triggered two SLAS token refreshes.)
 */
let inFlightDntValue: TrackingConsent | null = null;

/**
 * Whether `OneTrust.OnConsentChanged` has already been subscribed to on this document.
 *
 * Module scope, not effect scope: OneTrust exposes no way to *remove* a change listener, so a flag
 * living inside the effect closure would reset on remount and stack duplicates that cleanup cannot
 * undo. React StrictMode remounts every effect in development (`development.strictMode` is on), so
 * that was a real double-register locally rather than a hypothetical one.
 */
let changeListenerRegistered = false;

/** Reset module state. Test-only. */
export function resetConsentBridgeForTests(): void {
    hasReloadedForRevokedPerformance = false;
    inFlightDntValue = null;
    changeListenerRegistered = false;
}

/**
 * Drop the cached `dw_dnt` value when it disagrees with what the server actually holds.
 *
 * {@link DNT_SYNC_KEY} lives in `sessionStorage` and only records what *we* posted, so it can drift
 * out of step with the cookie: the auth middleware deletes `dw_dnt` whenever the stored value stops
 * matching the SLAS token's `dnt` claim, and nothing tells the client. Left alone, the stale cache
 * says "already synced" and the DNT signal stays lost for the rest of the tab session.
 *
 * The root loader ships the server's own view of tracking consent on every page load, so comparing
 * against it here makes the cache self-healing — any disagreement clears it and the next
 * {@link applyConsent} re-posts.
 *
 * @param serverTrackingConsent `auth.trackingConsent` from the root loader, or undefined when the
 * server holds no value.
 */
export function reconcileDntCache(serverTrackingConsent: TrackingConsent | undefined): void {
    const cached = getSessionJSONItem<TrackingConsent>(DNT_SYNC_KEY);
    if (cached !== undefined && cached !== serverTrackingConsent) {
        logger.debug('[OneTrust] Cached DNT value disagrees with the server, clearing to force a resync', {
            cached,
            serverTrackingConsent,
        });
        clearSessionJSONItem(DNT_SYNC_KEY);
    }
}

/**
 * Subscribe to OneTrust consent changes, at most once per document.
 *
 * Called from the consent bridge's callback rather than at mount, because the SDK has not installed
 * `window.OneTrust` yet when the component first renders. No-ops until it has.
 */
export function ensureConsentChangeListener(adobeLaunchSrc: string): void {
    if (changeListenerRegistered || typeof window.OneTrust?.OnConsentChanged !== 'function') {
        return;
    }
    changeListenerRegistered = true;
    window.OneTrust.OnConsentChanged(() => applyConsent(adobeLaunchSrc));
}

/**
 * Push the shopper's `C0003` (Functional) choice to Salesforce as the DNT signal.
 *
 * Reuses the action route the retired framework banner used: it refreshes the SLAS token with the
 * `dnt` claim embedded and lets the auth middleware write `dw_dnt`. This is the SCAPI counterpart
 * of the `__cq_dnt` cookie the SFRA OneTrust wrapper writes.
 *
 * Fails soft. If the post fails, nothing is cached, so the next consent callback retries.
 */
async function syncSalesforceDnt(granted: boolean): Promise<void> {
    const value = granted ? TrackingConsent.Accepted : TrackingConsent.Declined;
    if (inFlightDntValue === value || getSessionJSONItem<TrackingConsent>(DNT_SYNC_KEY) === value) {
        return;
    }
    // Claim the value before the first await, so a second callback in this same tick sees it.
    inFlightDntValue = value;

    try {
        const body = new FormData();
        body.set('trackingConsent', value);
        const response = await fetch(resourceRoutes.updateTrackingConsent, { method: 'POST', body });
        if (!response.ok) {
            logger.warn('[OneTrust] Failed to sync the Salesforce DNT signal', { status: response.status });
            return;
        }
        setSessionJSONItem(DNT_SYNC_KEY, value);
    } catch (error) {
        logger.warn('[OneTrust] Failed to sync the Salesforce DNT signal', { error });
    } finally {
        // Released either way: on success the session cache takes over, and on failure nothing is
        // cached so the next consent callback retries.
        if (inFlightDntValue === value) {
            inFlightDntValue = null;
        }
    }
}

/**
 * Discard page views queued for Adobe Launch while consent was undetermined.
 *
 * Only safe before Launch has loaded, while `adobeDataLayer` is still the plain array `root.tsx`
 * created. Once Launch initialises, the Adobe Client Data Layer replaces it with its own object and
 * truncating that would corrupt the library's internal state.
 */
function clearQueuedDataLayer(): void {
    const queue = window.adobeDataLayer;
    if (Array.isArray(queue) && !isAdobeLaunchLoaded()) {
        queue.length = 0;
    }
}

/**
 * Apply the shopper's current OneTrust choices to every tag this storefront gates.
 *
 * Idempotent — it reads the full consent state and drives each vendor to match, so it is safe to
 * call on load, on every consent change, and repeatedly.
 */
export function applyConsent(adobeLaunchSrc: string): void {
    const activeGroups = getActiveGroups();

    // C0002 Performance — Adobe Analytics, delivered by the Launch embed.
    if (hasCategory(OneTrustCategory.Performance, activeGroups)) {
        void injectAdobeLaunch(adobeLaunchSrc);
    } else {
        deleteCookiesByPrefix(CATEGORY_COOKIE_PREFIXES[OneTrustCategory.Performance] ?? []);
        clearQueuedDataLayer();
        // A tag manager cannot be unloaded: once Launch is running it owns its own rules and
        // beacons. Deleting the cookies alone would leave it tracking for the rest of the visit,
        // so revoking mid-session has to be a reload — after which the gate above keeps it off.
        if (isAdobeLaunchLoaded() && !hasReloadedForRevokedPerformance) {
            hasReloadedForRevokedPerformance = true;
            window.location.reload();
            return;
        }
    }

    // C0003 Functional — the Salesforce DNT signal, mirroring SFRA.
    void syncSalesforceDnt(hasCategory(OneTrustCategory.Functional, activeGroups));

    // C0004 Targeting — Attentive. The tag itself always loads so the SMS sign-up unit can render
    // (its identity cookies are classified Strictly Necessary); only tracking is gated here.
    const targetingGranted = hasCategory(OneTrustCategory.Targeting, activeGroups);
    setAttentiveAnalyticsConsent(targetingGranted);
    if (!targetingGranted) {
        clearAttentiveCookies();
    }
}
